// Stock reservado — Inventario → Operaciones → Reservas.
//
// Una reserva no mueve stock: el kardex sigue explicando el stock físico y la
// reserva vive en un libro aparte. Lo que cambia es lo que se puede prometer:
//
//     disponible = físico − reservado por otros
//
// Toda reserva caduca. Una sin fecha aparta producto para siempre por una
// cotización que nadie llegó a aceptar.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookmarkPlus, Loader2, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import {
  BuscadorProducto, Campo, Location, Modal, ProductoOpt, SortTh, fieldCls, invRpc,
  invalidateInventario, locationsList, num, productosOptList, toNum, useSort,
} from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Reserva {
  id: string; location_id: string; bodega: string; producto_id: string;
  codigo: string; producto: string; unidad_medida: string | null;
  unidades: number; consumidas: number; pendiente: number;
  origen: string; origen_id: string | null; documento: string | null; motivo: string | null;
  estado: string; vence_at: string; created_at: string; stock_bodega: number;
}

const ESTADO: Record<string, string> = {
  activa: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  consumida: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  liberada: 'bg-muted text-muted-foreground',
  vencida: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
};
const ORIGENES = [
  { v: 'cotizacion', t: 'Cotización' },
  { v: 'pedido', t: 'Pedido / tienda' },
  { v: 'transferencia', t: 'Transferencia planificada' },
  { v: 'produccion', t: 'Orden de producción' },
  { v: 'manual', t: 'Apartado manual' },
];

export function Reservas() {
  const qc = useQueryClient();
  const [loc, setLoc] = useState('');
  const [estado, setEstado] = useState('activa');
  const [nueva, setNueva] = useState(false);

  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: rows = [], isFetching } = useQuery<Reserva[]>({
    queryKey: ['inv-reservas', loc, estado],
    queryFn: () => invRpc<Reserva[]>('inv_reservas', { _location: loc || null, _estado: estado }),
  });
  const sort = useSort<Reserva>(rows, (r, k) => (r as unknown as Record<string, string | number | null>)[k] ?? null);

  const liberar = useMutation({
    mutationFn: (id: string) => invRpc('inv_reserva_liberar', { _id: id, _motivo: 'Liberada desde la pantalla' }),
    onSuccess: () => { toast.success('Reserva liberada'); invalidateInventario(qc); void qc.invalidateQueries({ queryKey: ['inv-reservas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalPend = rows.filter((r) => r.estado === 'activa').reduce((a, r) => a + Number(r.pendiente), 0);

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Stock reservado</h1>
          <p className="text-sm text-muted-foreground">
            Lo apartado para un documento deja de estar disponible para los demás. Sin esto, el mismo
            producto se le promete a dos clientes.
          </p>
        </div>
        <Button onClick={() => setNueva(true)}><BookmarkPlus className="mr-2 h-4 w-4" />Reservar</Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Campo label="Bodega" className="w-60">
          <select className={fieldCls} value={loc} onChange={(e) => setLoc(e.target.value)}>
            <option value="">Todas mis bodegas</option>
            {bodegas.filter((b) => b.es_activo).map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </Campo>
        <Campo label="Estado" className="w-48">
          <select className={fieldCls} value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="activa">Activas</option>
            <option value="vencida">Vencidas</option>
            <option value="consumida">Consumidas</option>
            <option value="liberada">Liberadas</option>
            <option value="todas">Todas</option>
          </select>
        </Campo>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">
          Reservas ({rows.length}){estado === 'activa' && totalPend > 0 && (
            <span className="ml-2 font-normal text-muted-foreground">· {num(totalPend)} unidades apartadas</span>)}
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>}
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-2 pr-3 text-right">N°</th>
                <SortTh col="bodega" sort={sort}>Bodega</SortTh>
                <SortTh col="producto" sort={sort}>Producto</SortTh>
                <SortTh col="origen" sort={sort}>Origen</SortTh>
                <SortTh col="documento" sort={sort}>Documento</SortTh>
                <SortTh col="pendiente" sort={sort} align="right">Apartadas</SortTh>
                <SortTh col="stock_bodega" sort={sort} align="right">Stock</SortTh>
                <SortTh col="vence_at" sort={sort}>Vence</SortTh>
                <SortTh col="estado" sort={sort}>Estado</SortTh>
                <th />
              </tr>
            </thead>
            <tbody>
              {!isFetching && rows.length === 0 && (
                <tr><td colSpan={10} className="py-10 text-center text-muted-foreground">
                  No hay reservas {estado === 'activa' ? 'activas' : ''}. Todo el stock está disponible.
                </td></tr>)}
              {sort.sorted.map((r, i) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-xs text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{r.bodega}</td>
                  <td className="py-2 pr-3"><span className="font-mono text-xs">{r.codigo}</span> {r.producto}</td>
                  <td className="py-2 pr-3">{ORIGENES.find((o) => o.v === r.origen)?.t ?? r.origen}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.documento ?? '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {num(r.pendiente)}
                    {r.consumidas > 0 && <span className="ml-1 text-[11px] text-muted-foreground">de {num(r.unidades)}</span>}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">{num(r.stock_bodega)}</td>
                  <td className={`py-2 pr-3 ${r.estado === 'vencida' ? 'text-amber-700 dark:text-amber-400' : ''}`}>
                    {new Date(r.vence_at).toLocaleDateString(LOCALE)}
                  </td>
                  <td className="py-2 pr-3">
                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${ESTADO[r.estado] ?? ''}`}>{r.estado}</span>
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {r.estado === 'activa' && (
                      <Button size="sm" variant="outline" disabled={liberar.isPending} onClick={() => liberar.mutate(r.id)}>
                        <Unlock className="mr-1 h-3.5 w-3.5" />Liberar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {nueva && <ModalReservar onClose={() => setNueva(false)} onHecho={() => {
        setNueva(false); invalidateInventario(qc); void qc.invalidateQueries({ queryKey: ['inv-reservas'] });
      }} />}
    </div>
  );
}

function ModalReservar({ onClose, onHecho }: { onClose: () => void; onHecho: () => void }) {
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: productos = [] } = useQuery<ProductoOpt[]>({ queryKey: ['inv-productos-opt'], queryFn: productosOptList });
  const [loc, setLoc] = useState('');
  const [prod, setProd] = useState('');
  const [cant, setCant] = useState(1);
  const [origen, setOrigen] = useState('manual');
  const [doc, setDoc] = useState('');
  const [dias, setDias] = useState(7);
  const [motivo, setMotivo] = useState('');

  // Lo que se puede prometer, no lo que hay: es el número que importa aquí.
  const { data: disp } = useQuery<{ fisico: number; reservado: number; disponible: number }>({
    queryKey: ['inv-disponible', loc, prod],
    queryFn: () => invRpc('inv_disponible', { _location: loc, _producto: prod }),
    enabled: !!loc && !!prod,
  });

  const guardar = useMutation({
    mutationFn: () => invRpc<{ disponible_restante: number }>('inv_reservar', {
      payload: {
        location_id: loc, producto_id: prod, unidades: cant, origen,
        documento: doc || null, dias, motivo: motivo || null,
        // Un apartado manual es su propio documento: sin origen_id, cualquier
        // salida ajena lo respeta, que es justo lo que se quiere.
        origen_id: null,
      },
    }),
    onSuccess: (r) => { toast.success(`Reservado. Quedan ${num(r.disponible_restante)} disponibles.`); onHecho(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const excede = !!disp && cant > Number(disp.disponible);
  const listo = !!loc && !!prod && cant > 0 && dias > 0 && !excede;

  return (
    <Modal title="Reservar stock" onClose={onClose}>
      <div className="space-y-3">
        <Campo label="Bodega">
          <select className={fieldCls} value={loc} onChange={(e) => setLoc(e.target.value)}>
            <option value="">Selecciona…</option>
            {bodegas.filter((b) => b.es_activo).map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </Campo>
        <Campo label="Producto">
          <BuscadorProducto value={prod} onChange={setProd} productos={productos} allowClear />
        </Campo>

        {disp && (
          <div className="grid grid-cols-3 gap-2 rounded-md border p-3 text-center text-sm">
            <div><span className="block text-xs text-muted-foreground">En bodega</span>
              <span className="tabular-nums">{num(disp.fisico)}</span></div>
            <div><span className="block text-xs text-muted-foreground">Ya reservado</span>
              <span className="tabular-nums">{num(disp.reservado)}</span></div>
            <div><span className="block text-xs text-muted-foreground">Disponible</span>
              <span className="font-semibold tabular-nums">{num(disp.disponible)}</span></div>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Campo label="Unidades">
            <input type="number" min={0} step="0.0001" className={fieldCls} value={cant}
                   onChange={(e) => setCant(toNum(e.target.value))} />
          </Campo>
          <Campo label="Caduca en (días)">
            <input type="number" min={1} className={fieldCls} value={dias}
                   onChange={(e) => setDias(toNum(e.target.value))} />
          </Campo>
        </div>
        {excede && (
          <p className="text-xs text-rose-600">
            Sólo hay {num(disp!.disponible)} disponibles: el resto ya está reservado para otro documento.
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Campo label="Origen">
            <select className={fieldCls} value={origen} onChange={(e) => setOrigen(e.target.value)}>
              {ORIGENES.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
            </select>
          </Campo>
          <Campo label="Documento">
            <input className={fieldCls} value={doc} onChange={(e) => setDoc(e.target.value)}
                   placeholder="Ej.: COT-000123" />
          </Campo>
        </div>
        <Campo label="Motivo">
          <input className={fieldCls} value={motivo} onChange={(e) => setMotivo(e.target.value)}
                 placeholder="Para quién y por qué se aparta" />
        </Campo>

        <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
          La reserva no mueve stock: el producto sigue en bodega y en el kardex. Lo que cambia es que
          deja de poder prometerse a otro. Al caducar se libera sola.
        </p>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!listo || guardar.isPending} onClick={() => guardar.mutate()}>Reservar</Button>
        </div>
      </div>
    </Modal>
  );
}
